import { FC, FormEvent, ReactElement, useState } from 'react'
import type { CommentResponse } from '@stream-io/feeds-client'
import classnames from 'classnames'
import { formatDistanceToNow } from 'date-fns'

import { Avatar, Button, Textarea } from '@/components'
import { ConfirmModal } from '@/compositions'
import { getDateFnsLocale } from '@/helpers/date'
import { trans } from '@/helpers/trans'

import './CommentListItem.scss'

interface CommentListItemProps {
    comment: CommentResponse
    isOwnComment: boolean
    onSave: (commentId: string, text: string) => void
    onDelete: (commentId: string) => void
}

export const CommentListItem: FC<CommentListItemProps> = ({
    comment,
    isOwnComment,
    onSave,
    onDelete,
}): ReactElement => {
    const [isEditing, setIsEditing] = useState(false)
    const [draft, setDraft] = useState(comment.text ?? '')
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)

    const startEditing = (): void => {
        setDraft(comment.text ?? '')
        setIsEditing(true)
    }

    const submitEdit = (): void => {
        const text = draft.trim()

        if (! text) {
            return
        }

        onSave(comment.id, text)
        setIsEditing(false)
    }

    const handleSubmit = (event: FormEvent): void => {
        event.preventDefault()
        submitEdit()
    }

    const handleConfirmDelete = (): void => {
        setIsConfirmingDelete(false)
        onDelete(comment.id)
    }

    return (
        <div className="comment-list-item">
            <Avatar
                className="comment-list-item__avatar"
                image={comment.user.image}
                initials={comment.user.name?.charAt(0)}
                name={comment.user.name}
            />
            <div className={classnames('comment-list-item__body', { 'comment-list-item__body--editing': isEditing })}>
                <div className="comment-list-item__meta">
                    <span className="comment-list-item__author">{comment.user.name ?? comment.user.id}</span>
                    <time className="comment-list-item__time" dateTime={comment.created_at.toISOString()}>
                        {formatDistanceToNow(comment.created_at, { addSuffix: true, locale: getDateFnsLocale() })}
                    </time>
                </div>

                {isEditing ? (
                    <form className="comment-list-item__edit-form" onSubmit={handleSubmit}>
                        <Textarea
                            className="comment-list-item__edit-input"
                            value={draft}
                            onChange={event => setDraft(event.target.value)}
                            autoGrow
                            minRows={1}
                            maxRows={6}
                            spellCheck
                            autoCorrect="on"
                            autoCapitalize="sentences"
                            onSubmit={submitEdit}
                        />
                        <div className="comment-list-item__edit-actions">
                            <Button
                                type="submit"
                                text={trans('containers.newsfeedContainer.save')}
                                className="comment-list-item__save"
                                disabled={! draft.trim()}
                            />
                            <Button
                                type="button"
                                text={trans('common.cancel')}
                                className="comment-list-item__cancel"
                                onClick={() => setIsEditing(false)}
                            />
                        </div>
                    </form>
                ) : (
                    <>
                        {comment.text && (
                            <p className="comment-list-item__text">{comment.text}</p>
                        )}

                        {isOwnComment && (
                            <div className="comment-list-item__actions">
                                <Button
                                    type="button"
                                    text={trans('common.edit')}
                                    className="comment-list-item__action"
                                    onClick={startEditing}
                                />
                                <Button
                                    type="button"
                                    text={trans('containers.newsfeedContainer.delete')}
                                    className="comment-list-item__action"
                                    onClick={() => setIsConfirmingDelete(true)}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            {isConfirmingDelete && (
                <ConfirmModal
                    title={trans('containers.newsfeedContainer.delete')}
                    message={trans('containers.newsfeedContainer.deleteCommentConfirm')}
                    confirmText={trans('containers.newsfeedContainer.delete')}
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setIsConfirmingDelete(false)}
                />
            )}
        </div>
    )
}
